import { nostojs } from "@nosto/nosto-js"
import { getText } from "@/utils/fetch"
import { createShopifyUrl } from "@/utils/createShopifyUrl"
import { customElement } from "../decorators"
import { NostoElement } from "../Element"
import { addRequest } from "../Campaign/orchestrator"
import { JSONResult } from "@nosto/nosto-js/client"

/**
 * A custom element that fetches Nosto placement results and renders them using Shopify section templates.
 * This component integrates with Shopify's Section Rendering API to dynamically render campaign content.
 *
 * @property {string} placement - The placement identifier for the campaign.
 * @property {string} section - The section to be used for Section Rendering API based rendering.
 *
 * @example
 * Basic usage with Shopify section rendering:
 * ```html
 * <nosto-section-campaign placement="front-page" section="product-recommendations"></nosto-section-campaign>
 * ```
 *
 * @example
 * Advanced usage for specific product page recommendations:
 * ```html
 * <nosto-section-campaign placement="product-page-cross-sell" section="related-products-section"></nosto-section-campaign>
 * ```
 */
@customElement("nosto-section-campaign")
export class SectionCampaign extends NostoElement {
  /** @private */
  static attributes = {
    placement: String,
    section: String
  }

  placement!: string
  section!: string

  async connectedCallback() {
    this.toggleAttribute("loading", true)
    try {
      await this.#initializeMarkup()
    } finally {
      this.toggleAttribute("loading", false)
    }
  }

  async #initializeMarkup() {
    const api = await new Promise(nostojs)
    const rec = (await addRequest({
      placement: this.placement,
      responseMode: "JSON_ORIGINAL" // TODO use a responseMode that returns only the needed data
    })) as JSONResult
    if (!rec) {
      return
    }
    const markup = await getSectionMarkup(this, rec)
    this.innerHTML = markup
    api.attributeProductClicksInCampaign(this, rec)
  }
}

async function getSectionMarkup(element: SectionCampaign, rec: JSONResult) {
  const handles = rec.products.map(product => product.handle).join(":")
  const target = createShopifyUrl("search")
  target.searchParams.set("section_id", element.section)
  target.searchParams.set("q", handles)
  const sectionHtml = await getText(target.href)
  const parser = new DOMParser()
  const doc = parser.parseFromString(sectionHtml, "text/html")
  if (rec.title) {
    const headingEl = doc.querySelector("[nosto-title]")
    if (headingEl) {
      headingEl.textContent = rec.title
    }
  }
  return doc.body.firstElementChild?.innerHTML?.trim() || sectionHtml
}

declare global {
  interface HTMLElementTagNameMap {
    "nosto-section-campaign": SectionCampaign
  }
}
